/*eslint eqeqeq: "off"*/

import './freq-lines.scss';

import Kapsule from 'kapsule';

import {
  ascending as d3Ascending,
  max as d3Max,
  min as d3Min,
  range as d3Range,
  axisBottom as d3AxisBottom,
  axisLeft as d3AxisLeft,
  axisRight as d3AxisRight,
  axisTop as d3AxisTop,
  scaleOrdinal as d3ScaleOrdinal,
  scaleSequential as d3ScaleSequential,
  scalePoint as d3ScalePoint,
  scaleTime as d3ScaleTime,
  event as d3Event,
  mouse as d3Mouse,
  select as d3Select,
  interpolateRdYlBu,
} from 'd3';

import d3Tip from 'd3-tip';

import {moveToFront as MoveToFront} from 'svg-utils';
import FreqOverview from './freq-overview.js';
import {chunksForArray, getReadableFreq} from "../../utils";


export default Kapsule({
  props: {
    data: {
      default: [],
      onChange(data, state) {
        parseData(data);

        state.zoomX = [
          d3Min(state.completeFlatData, d => d.valueRange[0]),
          d3Max(state.completeFlatData, d => d.valueRange[1])
        ];

        state.zoomY = [null, null];

        if (state.overviewArea) {
          state.overviewArea
            .domainRange(state.zoomX)
            .currentSelection(state.zoomX);
        }

        function parseData(rawData) {
          state.completeStructData = [];
          state.completeFlatData = [];
          state.totalNLines = 0;
          const frequencyList = [];

          for (let i = 0, ilen = rawData.length; i < ilen; i++) {
            const group = rawData[i].group;
            state.completeStructData.push({
              group: group,
              lines: rawData[i].data.map(({ label }) => label),
              val1s: rawData[i].data.map(({ val1 }) => val1),
              val2s: rawData[i].data.map(({ val2 }) => val2),
              val3s: rawData[i].data.map(({ val3 }) => val3),
            });

            for (let j = 0, jlen = rawData[i].data.length; j < jlen; j++) {
              const labelObject = rawData[i].data[j];
              for (let k = 0, klen = labelObject.data.length; k < klen; k++) {
                const labelDataObject = labelObject.data[k];
                state.completeFlatData.push({
                  group: group,
                  label: labelObject.label,
                  valueRange: labelDataObject.valueRange.map(d => d),
                  val: labelDataObject.val,
                  val1: labelObject.val1,
                  val2: labelObject.val2,
                  val3: labelObject.val3,
                });
                frequencyList.push(labelDataObject.valueRange);
              }
              state.totalNLines++;
            }
          }

          state.completeFrequencyList = [...new Set(frequencyList.flat())].sort(d3Ascending);

          if ( state.segmentClickedTooltip ) {
            state.segmentClickedTooltip.hide();
          }
        }
      }
    },
    width: {default: window.innerWidth},
    maxHeight: {default: 640},
    maxLineHeight: {default: 12},
    leftMargin: {default: 200},
    rightMargin: {default: 240},
    topMargin: {default: 40},
    bottomMargin: {default: 30},
    xTickFormat: {},
    freqMarker: {},
    zoomX: {  // Which time-range to show (null = min/max)
      default: [null, null],
      onChange(zoomX, state) {
        if (state.svg)
          state.svg.dispatch('zoom', {
            detail: {
              zoomX: zoomX,
              zoomY: null,
              redraw: false
            }
          });
      }
    },
    zoomY: {  // Which lines to show (null = min/max) [0 indexed]
      default: [null, null],
      onChange(zoomY, state) {
        if (state.svg)
          state.svg.dispatch('zoom', {
            detail: {
              zoomX: null,
              zoomY: zoomY,
              redraw: false
            }
          });
      }
    },
    minSegmentDuration: {},
    enableGuide: {
      default: false,
      onChange(val, state) {
        const opacity = val ? 1 : 0;
        d3Select('path.mouse-line').style('opacity', opacity);
        d3Select('text.mouse-text').style('opacity', opacity);
      }
    },
    enableSpace: {default: false},
    enableDraw: {
      default: false,
      onChange(val, state) {
        state.drawFrequencyList = [];
        state.drawIntervalFrequencyList = [];
      }
    },
    zColorScale: {default: d3ScaleSequential(interpolateRdYlBu)},
    zDataLabel: {default: '', triggerUpdate: false},  // Units of z data. Used in the tooltip descriptions
    zScaleLabel: {default: '', triggerUpdate: false}, // Units of colorScale. Used in the legend label
    enableOverview: {default: true}, // True/False
    enableAnimations: {
      default: true,
      onChange(val, state) {
        state.transDuration = val ? 700 : 0;
      }
    },

    // Callbacks
    onZoom: {},          // When user zooms in / resets zoom. Returns ([startX, endX], [startY, endY])
    onLabelClick: {},    // When user clicks on a group or y label. Returns (group) or (label, group) respectively
    onSegmentClick: {},  // When user clicks on a segment. Returns (segment object) respectively
    tooltipHtml: {},
  },

  methods: {
    refresh(state) {
      state._rerender();
      return this;
    },
    resetZoom(state) {
      state.svg.dispatch('resetZoom');
      return this;
    },
    hideClickedTooltip(state) {
      if ( state.segmentClickedTooltip) {
        state.segmentClickedTooltip.hide();
      }
      if(state.segmentTooltip){
        state.segmentTooltip.hide();
      }

      return this;
    }
  },

  stateInit: {
    height: null,
    overviewHeight: 20, // Height of overview section in bottom
    minLabelFont: 2,

    yScale: null,
    grpScale: null,

    xAxis: null,
    xGrid: null,
    yAxis: null,
    y1Axis: null,
    y2Axis: null,
    y3Axis: null,
    grpAxis: null,

    freqMarkerLine: null,

    svg: null,
    graph: null,
    overviewAreaElem: null,
    overviewArea: null,

    graphW: null,
    graphH: null,

    completeStructData: null,
    structData: null,
    completeFlatData: null,
    flatData: null,
    totalNLines: null,
    nLines: null,

    drawFrequencyList: null,
    drawIntervalFrequencyList: null,
    completeFrequencyList: null,

    minSegmentDuration: 0, // ms

    transDuration: 350,   // ms for transition duration
  },

  init(el, state) {
    const elem = d3Select(el)
      .attr('class', 'freq-chart');

    state.svg = elem.append('svg');
    state.overviewAreaElem = elem.append('div');

    // Initialize scales and axes
    state.yScale = d3ScalePoint();
    state.y1Scale = d3ScalePoint();
    state.y2Scale = d3ScalePoint();
    state.y3Scale = d3ScalePoint();
    state.grpScale = d3ScaleOrdinal();
    state.xAxis = d3AxisBottom();
    state.xGrid = d3AxisTop();
    state.spaceTicksGrid = d3AxisTop();
    state.drawTicksGrid = d3AxisTop();
    state.yAxis = d3AxisRight();
    state.y1Axis = d3AxisRight();
    state.y2Axis = d3AxisRight();
    state.y3Axis = d3AxisRight();
    state.grpAxis = d3AxisRight();

    addAxisLabels();
    addMouseLine();
    buildDomStructure();
    addTooltips();
    // addZoomSelection();
    setEvents();

    //

    function buildDomStructure() {
      state.yScale.invert = invertOrdinal;
      state.y1Scale.invert = invertOrdinal;
      state.y2Scale.invert = invertOrdinal;
      state.y3Scale.invert = invertOrdinal;
      state.grpScale.invert = invertOrdinal;

      const axises = state.svg.append('g').attr('class', 'axises');
      axises.append('g').attr('class', 'x-axis');
      axises.append('g').attr('class', 'x-grid');
      axises.append('g').attr('class', 'space-ticks-grid');
      axises.append('g').attr('class', 'draw-ticks-grid');
      axises.append('g').attr('class', 'y-axis');
      axises.append('g').attr('class', 'y1-axis');
      axises.append('g').attr('class', 'y2-axis');
      axises.append('g').attr('class', 'y3-axis');
      axises.append('g').attr('class', 'grp-axis');

      state.yAxis.scale(state.yScale).tickSize(0);
      state.y1Axis.scale(state.y1Scale).tickSize(0);
      state.y2Axis.scale(state.y2Scale).tickSize(0);
      state.y3Axis.scale(state.y3Scale).tickSize(0);

      state.grpAxis.scale(state.grpScale).tickSize(0);

      state.graph = state.svg.append('g');

      state.freqMarkerLine = state.svg.append('line').attr('class', 'x-axis-freq-marker');

      if (state.enableOverview) {
        addOverviewArea();
      }

      // Applies to ordinal scales (invert not supported in d3)
      function invertOrdinal(val, cmpFunc) {
        cmpFunc = cmpFunc || function (a, b) {
          return (a >= b);
        };

        const scDomain = this.domain();
        let scRange = this.range();

        if (scRange.length === 2 && scDomain.length !== 2) {
          // Special case, interpolate range vals
          scRange = d3Range(scRange[0], scRange[1], (scRange[1] - scRange[0]) / scDomain.length);
        }

        const bias = scRange[0];
        for (let i = 0, len = scRange.length; i < len; i++) {
          if (cmpFunc(scRange[i] + bias, val)) {
            return scDomain[Math.round(i * scDomain.length / scRange.length)];
          }
        }

        return this.domain()[this.domain().length - 1];
      }

      function addOverviewArea() {
        state.overviewArea = FreqOverview()
          .margins({top: 1, right: 120, bottom: 20, left: 80})
          .onChange((startValue, endValue) => {
            state.svg.dispatch('zoom', {
              detail: {
                zoomX: [startValue, endValue],
                zoomY: null
              }
            });
          })
          .domainRange(state.zoomX)
          .currentSelection(state.zoomX)(state.overviewAreaElem.node());

        state.svg.on('zoomScent', function () {
          const zoomX = d3Event.detail.zoomX;

          if (!state.overviewArea || !zoomX) return;

          // Out of overview bounds > extend it
          if (zoomX[0] < state.overviewArea.domainRange()[0] || zoomX[1] > state.overviewArea.domainRange()[1]) {
            state.overviewArea.domainRange([
              Math.min(zoomX[0], state.overviewArea.domainRange()[0]),
              Math.max(zoomX[1], state.overviewArea.domainRange()[1])
            ]);
          }

          state.overviewArea.currentSelection(zoomX);
        });
      }
    }

    function addTooltips() {
      // Group (주파수 용도)에 대한 Tooltip
      state.groupTooltip = d3Tip()
        .attr('class', 'chart-tooltip group-tooltip')
        .direction('w')
        .offset([0, 0])
        .html(d => {
          const leftPush = (d.hasOwnProperty('valueRange')
              ? state.xScale(d.valueRange[0])
              : 0
          );
          const topPush = (d.hasOwnProperty('label')
              ? state.grpScale(d.group) - state.yScale(d.group + '+&+' + d.label)
              : 0
          );
          state.groupTooltip.offset([topPush, -leftPush]);
          return d.group;
        });

      state.svg.call(state.groupTooltip);

      // FIXME: 제거. 우측 label에 대한 Tooltip
      // state.lineTooltip = d3Tip()
      //   .attr('class', 'chart-tooltip line-tooltip')
      //   .direction('e')
      //   .offset([0, 0])
      //   .html(d => {
      //     const rightPush = (d.hasOwnProperty('valueRange') ? state.xScale.range()[1] - state.xScale(d.valueRange[1]) : 0);
      //     state.lineTooltip.offset([0, rightPush]);
      //     return d.label;
      //   });
      // state.svg.call(state.lineTooltip);

      const makeValTooltip = (propertyName, ratio) => {
        const valTooltip = d3Tip()
          .attr('class', 'chart-tooltip line-tooltip')
          .direction('e')
          .offset([0, 0])
          .html((d) => {
            const rightDivide = state.rightMargin / 3;
            const rightPush = (d.hasOwnProperty('valueRange') ? state.xScale.range()[1] - state.xScale(d.valueRange[1]) : 0);
            valTooltip
              .offset([0, rightPush + (rightDivide * ratio)]);
            return `<div style="width: ${rightDivide-25}px; text-align: right">${d[propertyName] || "-"}</div>`;
          });
        return valTooltip;
      };
      state.val1Tooltip = makeValTooltip('val1', 0);
      state.svg.call(state.val1Tooltip);
      state.val2Tooltip = makeValTooltip('val2', 1);
      state.svg.call(state.val2Tooltip);
      state.val3Tooltip = makeValTooltip('val3', 2);
      state.svg.call(state.val3Tooltip);

      // 주파수 Segment에 대한 Tooltip
      state.segmentClickedTooltip = d3Tip()
        .attr('class', 'segment-tooltip segment-clicked-tooltip')
        .direction('s')
        .offset([5, 0])
        .html(d => {
          const html = state.tooltipHtml
            ? state.tooltipHtml(d.group, d.valueRange)
            : d.valueRange[0] + '<br/>' + d.valueRange[1];
          return `
            ${html}
            <a style="display: inline-block; position: absolute; right: 10px; top: 10px; font-size: 11px; color: #fff;"
               onclick="
                    this.parentElement.style.setProperty('opacity', 0);
                    this.parentElement.style.setProperty('pointer-events', 'none');
                    return false">
                닫기
            </a>
          `;
        });
      state.svg.call(state.segmentClickedTooltip);

      state.segmentTooltip = d3Tip()
        .attr('class', 'segment-tooltip')
        .direction('s')
        .offset([5, 0])
        .html(d => {
          const html = state.tooltipHtml
              ? state.tooltipHtml(d.group, d.valueRange)
              : d.valueRange[0] + '<br/>' + d.valueRange[1];
          return `
            ${html}
            <a style="display: inline-block; position: absolute; right: 10px; top: 10px; font-size: 11px; color: #fff;"
               onclick="
                    this.parentElement.style.setProperty('opacity', 0);
                    this.parentElement.style.setProperty('pointer-events', 'none');
                    return false">
                닫기
            </a>
          `;
        });
      state.svg.call(state.segmentTooltip);
    }

    function addAxisLabels() {
      state.svg.append('rect').attr('class', 'box-top');
      state.svg.append('rect').attr('class', 'box-top-left');
      state.svg.append('rect').attr('class', 'box-top-right');
      state.svg.append('rect').attr('class', 'box-left');
      state.svg.append('rect').attr('class', 'box-right');
      state.svg.append('line').attr('class', 'box-border box-top-1');
      state.svg.append('line').attr('class', 'box-border box-top-2');
      state.svg.append('line').attr('class', 'box-border box-left-1');
      state.svg.append('line').attr('class', 'box-border box-right-2');
      state.svg.append('line').attr('class', 'box-border box-right-3');
      state.svg.append('line').attr('class', 'box-border box-right-4');
      state.svg.append('line').attr('class', 'box-border box-bottom');

      state.svg.append('text').attr('class', 'axis-label1');
      state.svg.append('text').attr('class', 'axis-label2');
      state.svg.append('text').attr('class', 'axis-label3');
      state.svg.append('text').attr('class', 'axis-label4');
      state.svg.append('text').attr('class', 'axis-label5');
      state.svg.append('text').attr('class', 'axis-label6');
      state.svg.append('text').attr('class', 'axis-label7');
    }

    function addMouseLine() {
      state.svgRect = state.svg.append('svg:rect')
        .attr('fill', 'none')
        .attr('pointer-events', 'all')
        .on('mousemove', function() {
          if ( state.enableGuide ) {
            const e = this;
            const mouse = d3Mouse(e);
            let [x, y] = mouse;
            if (x < 0 || x > state.graphW || y < 0 || y > state.graphH)
              return;

            x += state.leftMargin;
            d3Select('path.mouse-line')
              .attr('d', () => {
                return `M${x}, ${state.graphH+state.topMargin + 5} ${x},${state.topMargin}`;
              });

            y += state.topMargin;
            d3Select('text.mouse-text')
              .attr('transform', function(d, i) {
                const freq = state.xScale.invert(x - state.leftMargin).getTime();  // FIXME: scaleTime 제거하기
                d3Select(this).text(getReadableFreq(freq));
                return `translate(${x}, ${y})`;
              });
          }
        })
        .on('click', function() {
          if ( state.enableDraw ) {
            const e = this;
            const mouse = d3Mouse(e);
            let [x, y] = mouse;
            if (x < 0 || x > state.graphW || y < 0 || y > state.graphH)
              return;

            const freq = state.xScale.invert(x).getTime();

            state.drawFrequencyList = (state.drawFrequencyList || []).concat([freq]).sort(d3Ascending);

            state.drawIntervalFrequencyList = chunksForArray(state.drawFrequencyList, 2)
              .filter(chunk => chunk.length === 2)
              .map(chunk => {
                const [beginFreq, endFreq] = chunk;
                const centerFreq = (beginFreq + endFreq) / 2;
                const intervalFreq = Math.abs(endFreq - beginFreq);
                return { beginFreq, endFreq, centerFreq, intervalFreq, y };
              });

            state._rerender();
          }
        })
      ;

      state.svg.append('g')
        .append('path')
          .attr('class', 'mouse-line')
          .style('stroke', '#ccc')
          .style('stroke-width', '1px')
          .style('stroke-dasharray', ('2, 3'))
          .style('opacity', 1);
      state.svg.append('text')
          .attr('class', 'mouse-text')
          .style('font-size', '12px')
          .style('opacity', 1);
    }

    // eslint-disable-next-line no-unused-vars
    function addZoomSelection() {
      state.graph.on('mousedown', function () {
        if (d3Select(window).on('mousemove.zoomRect') != null) // Selection already active
          return;

        const e = this;

        if (d3Mouse(e)[0] < 0 || d3Mouse(e)[0] > state.graphW || d3Mouse(e)[1] < 0 || d3Mouse(e)[1] > state.graphH)
          return;

        state.disableHover = true;

        const rect = state.graph.append('rect')
          .attr('class', 'chart-zoom-selection');

        const startCoords = d3Mouse(e);

        d3Select(window)
          .on('mousemove.zoomRect', function () {
            d3Event.stopPropagation();
            const newCoords = [
              Math.max(0, Math.min(state.graphW, d3Mouse(e)[0])),
              Math.max(0, Math.min(state.graphH, d3Mouse(e)[1]))
            ];
            rect.attr('x', Math.min(startCoords[0], newCoords[0]))
              .attr('y', Math.min(startCoords[1], newCoords[1]))
              .attr('width', Math.abs(newCoords[0] - startCoords[0]))
              .attr('height', Math.abs(newCoords[1] - startCoords[1]));

            state.svg.dispatch('zoomScent', {
              detail: {
                zoomX: [startCoords[0], newCoords[0]].sort(d3Ascending).map(state.xScale.invert),
                zoomY: [startCoords[1], newCoords[1]].sort(d3Ascending).map(d =>
                  state.yScale.domain().indexOf(state.yScale.invert(d))
                  + ((state.zoomY && state.zoomY[0]) ? state.zoomY[0] : 0)
                )
              }
            });
          })
          .on('mouseup.zoomRect', function () {
            d3Select(window).on('mousemove.zoomRect', null).on('mouseup.zoomRect', null);
            d3Select('body').classed('stat-noselect', false);
            rect.remove();
            state.disableHover = false;

            const endCoords = [
              Math.max(0, Math.min(state.graphW, d3Mouse(e)[0])),
              Math.max(0, Math.min(state.graphH, d3Mouse(e)[1]))
            ];

            if (startCoords[0] == endCoords[0] && startCoords[1] == endCoords[1])
              return;

            const newDomainX = [startCoords[0], endCoords[0]].sort(d3Ascending).map(state.xScale.invert);

            const newDomainY = [startCoords[1], endCoords[1]].sort(d3Ascending).map(d =>
              state.yScale.domain().indexOf(state.yScale.invert(d))
              + ((state.zoomY && state.zoomY[0]) ? state.zoomY[0] : 0)
            );

            const changeX = ((newDomainX[1] - newDomainX[0]) > (60 * 1000)); // Zoom damper
            const changeY = (newDomainY[0] != state.zoomY[0] || newDomainY[1] != state.zoomY[1]);

            if (changeX || changeY) {
              state.svg.dispatch('zoom', {
                detail: {
                  zoomX: changeX ? newDomainX : null,
                  zoomY: changeY ? newDomainY : null
                }
              });
            }
          }, true);

        d3Event.stopPropagation();
      });
    }

    function setEvents() {
      state.svg.on('zoom', function () {
        const evData = d3Event.detail,
          zoomX = evData.zoomX,
          zoomY = evData.zoomY,
          redraw = (evData.redraw == null) ? true : evData.redraw;

        if (!zoomX && !zoomY) return;

        if (zoomX) state.zoomX = zoomX;
        if (zoomY) state.zoomY = zoomY;

        state.svg.dispatch('zoomScent', {
          detail: {
            zoomX: zoomX,
            zoomY: zoomY
          }
        });

        if (!redraw) return;

        state._rerender();
        if (state.onZoom) state.onZoom(state.zoomX, state.zoomY);
      });

      state.svg.on('resetZoom', function () {
        const prevZoomX = state.zoomX;
        const prevZoomY = state.zoomY || [null, null];

        const newZoomX = state.enableOverview
          ? state.overviewArea.domainRange()
          : [
            d3Min(state.flatData, d => d.valueRange[0]),
            d3Max(state.flatData, d => d.valueRange[1])
          ],
          newZoomY = [null, null];

        if (prevZoomX[0] < newZoomX[0] || prevZoomX[1] > newZoomX[1]
          || prevZoomY[0] != newZoomY[0] || prevZoomY[1] != newZoomX[1]) {

          state.zoomX = [
            Math.min(prevZoomX[0], newZoomX[0]),
            Math.max(prevZoomX[1], newZoomX[1])
          ];
          state.zoomY = newZoomY;
          state.svg.dispatch('zoomScent', {
            detail: {
              zoomX: state.zoomX,
              zoomY: state.zoomY
            }
          });

          state._rerender();
        }

        if (state.onZoom) state.onZoom(null, null);
      });
    }
  },

  update(state) {

    applyFilters();
    setupDimensions();

    adjustXScale();
    adjustYScale();
    adjustGrpScale();

    renderMouseLine();

    renderSpaceTicksGrid();
    renderDrawTicksGrid();

    renderAxises();
    renderGroups();

    renderTimelines();
    adjustLegend();

    function applyFilters() {
      // Flat data based on segment length
      state.flatData = (state.minSegmentDuration > 0
          ? state.completeFlatData.filter(d => (d.valueRange[1] - d.valueRange[0]) >= state.minSegmentDuration)
          : state.completeFlatData
      );

      // zoomY
      if (state.zoomY == null || state.zoomY == [null, null]) {
        state.structData = state.completeStructData;
        state.nLines = 0;
        for (let i = 0, len = state.structData.length; i < len; i++) {
          state.nLines += state.structData[i].lines.length;
        }
        return;
      }

      state.structData = [];
      const cntDwn = [state.zoomY[0] == null ? 0 : state.zoomY[0]]; // Initial threshold
      cntDwn.push(Math.max(0, (state.zoomY[1] == null ? state.totalNLines : state.zoomY[1] + 1) - cntDwn[0])); // Number of lines
      state.nLines = cntDwn[1];
      for (let i = 0, len = state.completeStructData.length; i < len; i++) {
        let validLines = state.completeStructData[i].lines;
        let validVal1s = state.completeStructData[i].val1s;
        let validVal2s = state.completeStructData[i].val2s;
        let validVal3s = state.completeStructData[i].val3s;

        if (state.minSegmentDuration > 0) {  // Use only non-filtered (due to segment length) groups/labels
          if (!state.flatData.some(d => d.group === state.completeStructData[i].group)) {
            continue; // No data for this group
          }

          validLines = state.completeStructData[i].lines
            .filter(d => state.flatData.some(dd =>
              dd.group === state.completeStructData[i].group && dd.label === d
            ));

          validVal1s = state.completeStructData[i].val1s
            .filter(d => state.flatData.some(dd =>
              dd.group === state.completeStructData[i].group && dd.val1 === d
            ));

          validVal2s = state.completeStructData[i].val2s
            .filter(d => state.flatData.some(dd =>
              dd.group === state.completeStructData[i].group && dd.val2 === d
            ));

          validVal3s = state.completeStructData[i].val3s
            .filter(d => state.flatData.some(dd =>
              dd.group === state.completeStructData[i].group && dd.val3 === d
            ));
        }

        if (cntDwn[0] >= validLines.length) { // Ignore whole group (before start)
          cntDwn[0] -= validLines.length;
          continue;
        }

        const groupData = {
          group: state.completeStructData[i].group,
          lines: null,
          val1s: null,
          val2s: null,
          val3s: null,
        };

        if (validLines.length - cntDwn[0] >= cntDwn[1]) {  // Last (or first && last) group (partial)
          groupData.lines = validLines.slice(cntDwn[0], cntDwn[1] + cntDwn[0]);
          groupData.val1s = validVal1s.slice(cntDwn[0], cntDwn[1] + cntDwn[0]);
          groupData.val2s = validVal2s.slice(cntDwn[0], cntDwn[1] + cntDwn[0]);
          groupData.val3s = validVal3s.slice(cntDwn[0], cntDwn[1] + cntDwn[0]);
          state.structData.push(groupData);
          cntDwn[1] = 0;
          break;
        }

        if (cntDwn[0] > 0) {  // First group (partial)
          groupData.lines = validLines.slice(cntDwn[0]);
          groupData.val1s = validVal1s.slice(cntDwn[0]);
          groupData.val2s = validVal2s.slice(cntDwn[0]);
          groupData.val3s = validVal3s.slice(cntDwn[0]);
          cntDwn[0] = 0;
        } else {  // Middle group (full fit)
          groupData.lines = validLines;
          groupData.val1s = validVal1s;
          groupData.val2s = validVal2s;
          groupData.val3s = validVal3s;
        }

        state.structData.push(groupData);
        cntDwn[1] -= groupData.lines.length;
      }

      state.nLines -= cntDwn[1];
    }

    function setupDimensions() {
      state.graphW = state.width - state.leftMargin - state.rightMargin;
      state.graphH = d3Min([state.nLines * state.maxLineHeight, state.maxHeight - state.topMargin - state.bottomMargin]);
      state.height = state.graphH + state.topMargin + state.bottomMargin;

      state.svg.transition().duration(state.transDuration)
        .attr('width', state.width)
        .attr('height', state.height);

      state.graph.attr('transform', 'translate(' + state.leftMargin + ',' + state.topMargin + ')');

      if (state.overviewArea) {
        state.overviewArea
          .width(state.width * 0.8)
          .height(state.overviewHeight + state.overviewArea.margins().top + state.overviewArea.margins().bottom);
      }
    }

    function adjustXScale() {
      state.zoomX[0] = state.zoomX[0] || d3Min(state.flatData, d => d.valueRange[0]);
      state.zoomX[1] = state.zoomX[1] || d3Max(state.flatData, d => d.valueRange[1]);

      state.xScale = d3ScaleTime()
        .domain(state.zoomX)
        .range([0, state.graphW])
        .clamp(true);

      if (state.overviewArea) {
        state.overviewArea
          .scale(state.xScale.copy())
          .tickFormat(state.xTickFormat);
      }
    }

    function adjustYScale() {
      const adjust = (yScale, listFieldname) => {
        const values = state.structData.map(({ group, ...row }) =>
          row[listFieldname].map(value => group + '+&+' + value)
        ).flat();
        yScale
          .domain(values)
          .range([state.graphH / values.length * 0.5, state.graphH * (1 - 0.5 / values.length)]);
      };

      adjust(state.yScale, 'lines');
      adjust(state.y1Scale, 'val1s');
      adjust(state.y2Scale, 'val2s');
      adjust(state.y3Scale, 'val3s');
    }

    function adjustGrpScale() {
      state.grpScale.domain(state.structData.map(d => d.group));

      let cntLines = 0;
      state.grpScale.range(state.structData.map(d => {
        const pos = (cntLines + d.lines.length / 2) / state.nLines * state.graphH;
        cntLines += d.lines.length;
        return pos;
      }));
    }

    function adjustLegend() {
      state.svg.select('.legendG')
        .transition().duration(state.transDuration)
        .attr('transform', `translate(${state.leftMargin + state.graphW * 0.05},2)`);
    }

    function renderMouseLine() {
      const rightDivide = state.rightMargin / 3;

      state.svgRect
        .attr('width', state.graphW)
        .attr('height', state.graphH)
        .attr('transform', `translate(${state.leftMargin}, ${state.topMargin})`);

      const updateLabel = (selector, text, fontSize, dy, positionX, marginTop) => {
        state.svg.select(selector)
          .attr('transform', `translate(${positionX}, ${marginTop || 0})`)
          .attr('dy', dy)
          .style('font-size', fontSize)
          .style('text-anchor', 'middle')
          .text(text);
      };

      updateLabel('text.axis-label1', '용도', '12px', '1em',
        (state.leftMargin*0.5), 12);
      updateLabel('text.axis-label2', '주파수 이용현황', '12px', '1em',
        state.leftMargin + (state.width - state.leftMargin - state.rightMargin) * 0.5, 12);

      updateLabel('text.axis-label3', '무선국수', '12px', '1em',
        (state.width - state.rightMargin + rightDivide*0.5), 12);
      updateLabel('text.axis-label4', '대역폭', '12px', '1em',
        (state.width - state.rightMargin + rightDivide*1.5), 8);
      updateLabel('text.axis-label5', '(KHz)', '11px', '2.1em',
        (state.width - state.rightMargin + rightDivide*1.5), 8);
      updateLabel('text.axis-label6', '무선국집중도', '12px', '1em',
        (state.width - state.rightMargin + rightDivide*2.5), 8);
      updateLabel('text.axis-label7', '(국수/KHz)', '11px', '2.1em',
        (state.width - state.rightMargin + rightDivide*2.5), 8);

      state.svg.select('rect.box-top')
        .attr('rx', 1)
        .attr('ry', 1)
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', state.width)
        .attr('height', state.topMargin);

      state.svg.select('rect.box-top-left')
        .attr('rx', 1)
        .attr('ry', 1)
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', state.leftMargin)
        .attr('height', state.topMargin);

      state.svg.select('rect.box-top-right')
        .attr('rx', 1)
        .attr('ry', 1)
        .attr('x', state.width - state.rightMargin)
        .attr('y', 0)
        .attr('width', state.rightMargin)
        .attr('height', state.topMargin);

      state.svg.select('rect.box-left')
        .attr('rx', 1)
        .attr('ry', 1)
        .attr('x', 0)
        .attr('y', state.topMargin)
        .attr('width', state.leftMargin)
        .attr('height', state.height - state.topMargin - state.bottomMargin);

      state.svg.select('rect.box-right')
        .attr('rx', 1)
        .attr('ry', 1)
        .attr('x', state.width - state.rightMargin)
        .attr('y', state.topMargin)
        .attr('width', state.rightMargin)
        .attr('height', state.height - state.topMargin - state.bottomMargin);

      state.svg.select('line.box-top-1')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', state.width)
        .attr('y2', 0);

      state.svg.select('line.box-top-2')
        .attr('x1', 0)
        .attr('y1', state.topMargin)
        .attr('x2', state.width)
        .attr('y2', state.topMargin);

      state.svg.select('line.box-left-1')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', 0)
        .attr('y2', state.height - state.bottomMargin);

      state.svg.select('line.box-right-2')
        .attr('x1', state.width - state.rightMargin + rightDivide)
        .attr('y1', state.topMargin)
        .attr('x2', state.width - state.rightMargin + rightDivide)
        .attr('y2', state.height - state.bottomMargin);

      state.svg.select('line.box-right-3')
        .attr('x1', Math.floor(state.width - state.rightMargin + rightDivide * 2))
        .attr('y1', state.topMargin)
        .attr('x2', Math.floor(state.width - state.rightMargin + rightDivide * 2))
        .attr('y2', state.height - state.bottomMargin);

      state.svg.select('line.box-right-4')
        .attr('x1', state.width)
        .attr('y1', 0)
        .attr('x2', state.width)
        .attr('y2', state.height - state.bottomMargin);

      state.svg.select('line.box-bottom')
        .attr('x0', 0)
        .attr('y1', state.height - state.bottomMargin)
        .attr('x2', state.width)
        .attr('y2', state.height - state.bottomMargin + 1);

      let gridLines = state.svg.selectAll('rect.grid-line').data(
        // 마지막 Group은 line이 box-bottom과 겹치기에 제외
        state.structData.slice(0, state.structData.length - 1),
        d => d.group);
      gridLines.exit()
        .style('stroke-opacity', 0)
        .style('fill-opacity', 0)
        .remove();
      const newGridLines = gridLines.enter().append('rect')
        .attr('class', 'grid-line')
        .attr('x', 0)
        .attr('y', 0)
        .attr('height', 0);

      gridLines = gridLines.merge(newGridLines);
      gridLines
        .attr('width', state.width)
        .attr('height', d => {
          return state.graphH * d.lines.length / state.nLines;
        })
        .attr('y', d => {
          return state.topMargin + state.grpScale(d.group) - state.graphH * d.lines.length / state.nLines / 2;
        });
    }

    function renderSpaceTicksGrid() {
      if ( state.enableSpace ) {
        state.spaceTicksGrid
          .scale(state.xScale)
          .tickValues(state.completeFrequencyList)
          .tickFormat(state.xTickFormat)
          .tickSize(state.graphH);

        state.svg.select('g.space-ticks-grid')
          .attr('transform', `translate(0, ${state.graphH})`)
          .transition().duration(state.transDuration)
          .call(state.spaceTicksGrid)
          .style('opacity', 1);
      }
      else {
        state.svg.select('g.space-ticks-grid')
          .style('opacity', 0);
      }
    }

    function renderDrawTicksGrid() {
      const opacity = state.enableDraw ? 1 : 0;
      state.drawTicksGrid
        .scale(state.xScale)
        .tickValues(state.drawFrequencyList)
        .tickFormat(state.xTickFormat)
        .tickSize(state.graphH);

      state.svg.select('g.draw-ticks-grid')
        .attr('transform', `translate(0, ${state.graphH})`)
        .transition().duration(state.transDuration / 4)
        .call(state.drawTicksGrid)
        .style('opacity', opacity);

      const intervalFreq = state.graph.selectAll('text.interval-freq')
        .data(state.drawIntervalFrequencyList);

      intervalFreq.exit()
        .transition().duration(state.transDuration / 4)
        .style('opacity', 1)
        .remove();

      intervalFreq.enter().append('text')
        .attr('class', 'interval-freq')
        .style('text-anchor', 'middle');

      state.svg.selectAll('text.interval-freq')
        .attr('x', ({ centerFreq }) => state.xScale(centerFreq))
        .attr('y', ({ y }) => y)
        .text(({ intervalFreq }) => state.xTickFormat(intervalFreq));
    }

    function renderAxises() {
      state.svg.select('.axises')
        .attr('transform', 'translate(' + state.leftMargin + ',' + state.topMargin + ')');

      // X
      state.xAxis
        .scale(state.xScale)
        .ticks(Math.round(state.graphW * 0.0011))
        .tickFormat(state.xTickFormat);
      state.xGrid
        .scale(state.xScale)
        .ticks(state.xAxis.ticks()[0])
        .tickFormat('');

      state.svg.select('g.x-axis')
        .style('stroke-opacity', 0)
        .style('fill-opacity', 0)
        .attr('transform', 'translate(0,' + state.graphH + ')')
        .transition().duration(state.transDuration)
        .call(state.xAxis)
        .style('stroke-opacity', 1)
        .style('fill-opacity', 1);

      /* Angled x axis labels
       state.svg.select('g.x-axis').selectAll('text')
       .style('text-anchor', 'end')
       .attr('transform', 'translate(-10, 3) rotate(-60)');
       */

      state.xGrid.tickSize(state.graphH);
      state.svg.select('g.x-grid')
        .attr('transform', 'translate(0,' + state.graphH + ')')
        .transition().duration(state.transDuration)
        .call(state.xGrid);

      if (
        state.freqMarker &&
        state.freqMarker >= state.xScale.domain()[0] &&
        state.freqMarker <= state.xScale.domain()[1]
      ) {
        state.freqMarkerLine
          .style('display', 'block')
          .transition().duration(state.transDuration)
          .attr('x1', state.xScale(state.freqMarker) + state.leftMargin)
          .attr('x2', state.xScale(state.freqMarker) + state.leftMargin)
          .attr('y1', state.topMargin + 1)
          .attr('y2', state.graphH + state.topMargin)
      } else {
        state.freqMarkerLine.style('display', 'none');
      }

      // Y
      const fontVerticalMargin = 0.6;
      const labelDisplayRatio = Math.ceil(state.nLines * state.minLabelFont / Math.sqrt(2) / state.graphH / fontVerticalMargin);
      const rightDivide = state.rightMargin / 3;

      const setYAxisFactors = (selector, yAxis, yScale, distanceX, isHide) => {
        const tickVals = yScale.domain().filter((d, i) => !(i % labelDisplayRatio));
        let fontSize = Math.min(14, state.graphH / tickVals.length * fontVerticalMargin * Math.sqrt(2)) - 1;
        let maxChars = Math.ceil(rightDivide / (fontSize / Math.sqrt(2)));
        yAxis
          .tickFormat(d => reduceLabel(d.split('+&+')[1], maxChars))
          .tickValues(tickVals);
        const svg = state.svg.select(selector)
          .transition().duration(state.transDuration)
          .attr('transform', 'translate(' + (state.graphW + distanceX) + ', 0)')
          .style('font-size', fontSize + 'px')
          .style('text-anchor', 'end')
          .call(yAxis);
        if ( isHide )
          svg.style('opacity', 0);
      };

      setYAxisFactors('g.y-axis', state.yAxis, state.yScale, 0, true);
      setYAxisFactors('g.y1-axis', state.y1Axis, state.y1Scale, rightDivide*0.9);
      setYAxisFactors('g.y2-axis', state.y2Axis, state.y2Scale, rightDivide*1.9);
      setYAxisFactors('g.y3-axis', state.y3Axis, state.y3Scale, rightDivide*2.9);

      // Grp
      const minHeight = d3Min(state.grpScale.range(), function (d, i) {
        return i > 0 ? d - state.grpScale.range()[i - 1] : d * 2;
      });
      let fontSize = Math.min(12, minHeight * fontVerticalMargin * Math.sqrt(2));
      let maxChars = Math.floor(state.leftMargin / (fontSize / Math.sqrt(1.5)));

      state.grpAxis.tickFormat(d => reduceLabel(d, maxChars));
      state.svg.select('g.grp-axis')
        .transition().duration(state.transDuration)
        .style('font-size', fontSize + 'px')
        .style('letter-spacing', '-0.5px')
        .attr('transform', `translate(${-1 * (state.leftMargin)}, 0)`)
        .call(state.grpAxis);

      // Make Axises clickable
      // if (state.onLabelClick) {
      //   state.svg.selectAll('g.y-axis,g.grp-axis').selectAll('text')
      //     .style('cursor', 'pointer')
      //     .on('click', function (d) {
      //       const segms = d.split('+&+');
      //       state.onLabelClick(...segms.reverse());
      //     });
      // }

      //

      function reduceLabel(label, maxChars) {
        console.log(label + " / " + maxChars);
        return label.length <= maxChars
          ? label
          : (
            label.substring(0, maxChars - 4)
            + '...'
          );
      }
    }

    function renderGroups() {
      let groups = state.graph.selectAll('rect.series-group').data(state.structData, d => d.group);

      groups.exit()
        .transition().duration(state.transDuration)
        .style('stroke-opacity', 0)
        .style('fill-opacity', 0)
        .remove();

      // 차트 내 1 Row => Group
      const newGroups = groups.enter().append('rect')
        .attr('class', 'series-group')
        .attr('x', 0)
        .attr('y', 0)
        .attr('height', 0)
        .style('fill', 'none');
        // .style('stroke', '#ccc')
        // .style('stroke-width', 1)
        // .style("stroke-dasharray", ("2, 3"));

      groups = groups.merge(newGroups);

      groups.transition().duration(state.transDuration)
        .attr('width', state.graphW)
        .attr('height', function (d) {
          return state.graphH * d.lines.length / state.nLines;
        })
        .attr('y', function (d) {
          return state.grpScale(d.group) - state.graphH * d.lines.length / state.nLines / 2;
        });
    }

    function renderTimelines(maxElems) {
      if (maxElems < 0) maxElems = null;

      const hoverEnlargeRatio = .4;

      const dataFilter = (d, i) =>
        (maxElems == null || i < maxElems) &&
        (state.grpScale.domain().indexOf(d.group) + 1 &&
          d.valueRange[1] >= state.xScale.domain()[0] &&
          d.valueRange[0] <= state.xScale.domain()[1] &&
          state.yScale.domain().indexOf(d.group + '+&+' + d.label) + 1);

      state.lineHeight = state.graphH / state.nLines * 0.8;

      let timelines = state.graph.selectAll('rect.series-segment').data(
        state.flatData.filter(dataFilter),
        d => d.group + d.label + d.valueRange[0]
      );

      timelines.exit()
        .transition().duration(state.transDuration)
        .style('fill-opacity', 0)
        .remove();

      const newSegments = timelines.enter().append('rect')
        .attr('class', 'series-segment')
        .attr('rx', 1)
        .attr('ry', 1)
        .attr('x', state.graphW / 2)
        .attr('y', state.graphH / 2)
        .attr('width', 0)
        .attr('height', 0)
        .style('fill', d => state.zColorScale(d))
        .style('fill-opacity', 0)
        // .on('mouseover.groupTooltip', state.groupTooltip.show)
        // .on('mouseout.groupTooltip', state.groupTooltip.hide)
        // .on('mouseover.lineTooltip', state.lineTooltip.show)
        // .on('mouseout.lineTooltip', state.lineTooltip.hide)
        // .on('mouseover.val1Tooltip', state.val1Tooltip.show)
        // .on('mouseout.val1Tooltip', state.val1Tooltip.hide)
        // .on('mouseover.val2Tooltip', state.val2Tooltip.show)
        // .on('mouseout.val2Tooltip', state.val2Tooltip.hide)
        // .on('mouseover.val3Tooltip', state.val3Tooltip.show)
        // .on('mouseout.val3Tooltip', state.val3Tooltip.hide)
        .on('click.segmentClickedTooltip', state.segmentClickedTooltip.show)
        .on('mouseover.segmentTooltip', state.segmentTooltip.show)
        .on('mouseout.segmentTooltip', state.segmentTooltip.hide);

      newSegments
        .on('mouseover', function () {
          if ('disableHover' in state && state.disableHover)
            return;

          MoveToFront()(this);

          const hoverEnlarge = state.lineHeight * hoverEnlargeRatio;

          d3Select(this)
            .transition().duration(70)
            .attr('x', function (d) {
              return state.xScale(d.valueRange[0]) - hoverEnlarge / 2;
            })
            .attr('width', function (d) {
              return d3Max([1, state.xScale(d.valueRange[1]) - state.xScale(d.valueRange[0])]) + hoverEnlarge;
            })
            .attr('y', function (d) {
              return state.yScale(d.group + '+&+' + d.label) - (state.lineHeight + hoverEnlarge) / 2;
            })
            .attr('height', state.lineHeight + hoverEnlarge)
            .style('fill-opacity', 1);
        })
        .on('mouseout', function () {
          d3Select(this)
            .transition().duration(250)
            .attr('x', function (d) {
              return state.xScale(d.valueRange[0]);
            })
            .attr('width', function (d) {
              return d3Max([1, state.xScale(d.valueRange[1]) - state.xScale(d.valueRange[0])]);
            })
            .attr('y', function (d) {
              return state.yScale(d.group + '+&+' + d.label) - state.lineHeight / 2;
            })
            .attr('height', state.lineHeight)
            .style('fill-opacity', .8);
        })
        .on('click', function (s) {
          if (state.onSegmentClick)
            state.onSegmentClick(s);
        });

      timelines = timelines.merge(newSegments);

      timelines.transition().duration(state.transDuration)
        .attr('x', function (d) {
          return state.xScale(d.valueRange[0]);
        })
        .attr('width', function (d) {
          return d3Max([1, state.xScale(d.valueRange[1]) - state.xScale(d.valueRange[0])]);
        })
        .attr('y', function (d) {
          return state.yScale(d.group + '+&+' + d.label) - state.lineHeight / 2;
        })
        .attr('height', state.lineHeight)
        .style('fill-opacity', .8);
    }
  }
});
